export type ContentPhase = 'before' | 'during' | 'after';
export type DeliveryMode = 'passive_read' | 'optional_reflection' | 'script' | 'checklist' | 'grounding' | 'story';
export type StigmaVitalSign = 'disbelief_blame' | 'differential_treatment' | 'safety' | 'internalised_blame' | 'support';
export type ContentType = 'library_item' | 'script' | 'story';

export interface EDContentItem {
  id: string;
  type: ContentType;
  title: string;
  summary: string;
  body: string;
  script_lines?: string[];
  journey: 'ED';
  phase: ContentPhase | ContentPhase[];
  module: string[];
  stigma_vital_signs: StigmaVitalSign[];
  tags: string[];
  intensity: 1 | 2 | 3;
  delivery_mode: DeliveryMode[];
  estimated_time_minutes: number;
  notes_for_youth?: string;
  age_at_event?: number;
  self_described_identities?: string[];
  content_warnings?: string[];
  gif?: string;
}

export const ED_STAGES = [
  { id: 'considering', label: 'Thinking about going' },
  { id: 'on_the_way', label: 'On my way' },
  { id: 'waiting_triage', label: 'Waiting for triage' },
  { id: 'triage', label: 'At triage' },
  { id: 'waiting_bed', label: 'Waiting for a bed' },
  { id: 'being_assessed', label: 'Being assessed' },
  { id: 'waiting_treatment', label: 'Waiting for treatment' },
  { id: 'treatment', label: 'Getting treatment' },
  { id: 'observation', label: 'Under observation' },
  { id: 'discharge_prep', label: 'Getting ready to leave' },
  { id: 'after', label: 'After the visit' },
] as const;

export type EDStageId = typeof ED_STAGES[number]['id'];

export const ED_CONTENT: EDContentItem[] = [
  // ── BEFORE ────────────────────────────────────────────────────────────
  {
    id: 'ed_before_is_it_bad_enough_v1',
    type: 'library_item',
    title: '"Is this bad enough for ED?"',
    summary: 'Quick questions for when you\'re unsure about going to ED.',
    body: `Many Black and racialized youth with sickle cell are taught not to "make a fuss." That makes it harder to know when to go to ED, especially after being dismissed before.

Ask yourself:
• Is this like times I needed IV meds or admission?
• Have home meds stopped working or barely touched the pain?
• Is it hard to breathe, think clearly, or move?

If yes, it is reasonable to consider going in. Doubting yourself often comes from how you've been treated before — not from your body being wrong.

You do not have to be certain. You are allowed to go in and find out.`,
    journey: 'ED',
    phase: 'before',
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['safety', 'internalised_blame'],
    tags: ['ED', 'raceracism', 'poverty'],
    intensity: 1,
    delivery_mode: ['passive_read', 'optional_reflection'],
    estimated_time_minutes: 2,
  },
  {
    id: 'ed_before_emergency_profile_intro_v1',
    type: 'library_item',
    title: 'Your emergency profile',
    summary: 'Why an emergency profile exists and how it can reduce your labour in the ED.',
    body: `You should not have to retell your whole history every time you show up in pain. An emergency profile helps shift some of that work back onto the system.

It can include:
• Your sickle cell type and usual baseline
• What your crises usually look and feel like
• Medications and doses that have worked, and what hasn't
• Allergies and serious side effects
• Names of clinics or hematologists who know you

You can keep this on your phone and update it when you have the capacity — not in the middle of a crisis.

Having it doesn't guarantee you'll be believed. But it shifts the conversation from "prove you're sick" to "here is my care plan."`,
    journey: 'ED',
    phase: 'before',
    module: ['profile_identities_journeys', 'resources_stories_insights'],
    stigma_vital_signs: ['disbelief_blame', 'differential_treatment', 'safety'],
    tags: ['ED', 'raceracism', 'drug_seeking', 'non_compliant'],
    intensity: 1,
    delivery_mode: ['passive_read', 'checklist'],
    estimated_time_minutes: 2,
  },
  {
    id: 'ed_before_triage_overview_v1',
    type: 'library_item',
    title: 'What usually happens at triage — and why it can feel harsh',
    summary: 'A plain-language walk-through of triage that names racism, time-pressure, and power.',
    body: `In most emergency departments, the first step is triage — a quick check where a nurse decides how urgent your situation is.

In theory, triage is about how sick you are, not who you are. In practice, Black and other racialized people, disabled people, and youth are often seen as less credible, less urgent, or "coping fine" even when in severe pain.

You might notice:
• People who arrived after you being called in first
• Staff using short, clipped language or not making eye contact
• Being asked multiple times whether you've already taken drugs
• Having your pain score written down lower than what you said

These things come from anti-Black racism, ableism, and a triage system designed around bodies that are already seen as more credible.

None of this is your fault. And noticing it — even naming it quietly to yourself — is an act of clarity, not complaint.`,
    journey: 'ED',
    phase: 'before',
    module: ['resources_stories_insights'],
    stigma_vital_signs: ['disbelief_blame', 'differential_treatment', 'safety'],
    tags: ['ED', 'raceracism', 'long_waits_after_others', 'drug_seeking'],
    intensity: 2,
    delivery_mode: ['passive_read', 'optional_reflection'],
    estimated_time_minutes: 2,
  },

  // ── DURING ───────────────────────────────────────────────────────────
  {
    id: 'ed_during_waiting_grounding_v1',
    type: 'library_item',
    title: 'Getting through the wait',
    summary: 'Grounding and validation when you\'re waiting in pain and your fear is building.',
    body: `Long waits in ED are common, but not equal. Black and other racialized youth with sickle cell often wait while others are seen first, are watched by security, or treated as if they're exaggerating.

If your pain or fear is building, this is not you being "too sensitive." It is your body in distress inside a system that often disbelieves and criminalizes Black pain.

You might try one small step at a time:
• Notice one thing that feels even slightly supportive — a person nearby, a text from someone who knows you, a warm blanket, a song
• If you can breathe slowly, breathe out a little longer than you breathe in — it tells your nervous system the danger is not right here in your lungs
• You do not have to perform calm. You are allowed to hurt openly

You are not overreacting. The wait is real. The pain is real. The racism shaping this wait is real.`,
    journey: 'ED',
    phase: 'during',
    module: ['reflection_stigma_vital_signs', 'resources_stories_insights'],
    stigma_vital_signs: ['disbelief_blame', 'differential_treatment', 'safety', 'internalised_blame', 'support'],
    tags: ['ED', 'long_waits_after_others', 'raceracism', 'security_calledwatched'],
    intensity: 2,
    delivery_mode: ['grounding', 'optional_reflection'],
    estimated_time_minutes: 2,
    gif: '__breathing__',
  },
  {
    id: 'ed_during_security_safety_check_v1',
    type: 'library_item',
    title: 'When security is watching',
    summary: 'A quick safety check and options when security shows up around your care.',
    body: `Security near you while you're in pain is not neutral. For Black and other racialized youth, security in hospitals is tied to policing, surveillance, and criminalization.

If security is watching or has been called, ask yourself:
• How safe do I feel speaking honestly or asking questions right now?
• Is there anyone with me — in person or by phone — who can be a witness or speak on my behalf?

If your safety feels low, it is reasonable to:
• Give shorter answers and conserve energy
• Let a support person speak, if you want that
• Focus on getting through, not on challenging anything in this moment
• Log what happened after the visit when you're safer

You are not required to be brave right now. Survival is the goal.`,
    journey: 'ED',
    phase: 'during',
    module: ['reflection_stigma_vital_signs', 'resources_stories_insights'],
    stigma_vital_signs: ['safety', 'disbelief_blame', 'differential_treatment', 'internalised_blame'],
    tags: ['ED', 'security_calledwatched', 'raceracism', 'dangerous_aggressive'],
    intensity: 3,
    delivery_mode: ['grounding', 'optional_reflection'],
    estimated_time_minutes: 2,
  },

  // ── SCRIPTS ──────────────────────────────────────────────────────────
  {
    id: 'script_ed_drug_seeking_physician_v1',
    type: 'script',
    title: 'When they hint you\'re "drug-seeking"',
    summary: 'Short phrases for if staff frame you as there for drugs, not care.',
    body: '',
    script_lines: [
      '"I\'m here because I\'m in sickle cell pain. This level is not new for me, and I know what usually helps. I need my pain treated quickly and safely."',
      '"My chart and emergency plan describe the meds and doses that work in my crises. Can we follow that plan, or can you explain why not?"',
      '"It\'s harmful when my pain is seen as drug-seeking instead of as sickle cell disease. I need that to be clear in my chart."',
      'If someone is with you: "Can you help say what I need right now? I\'m in too much pain to keep explaining."',
    ],
    journey: 'ED',
    phase: 'during',
    module: ['resources_stories_insights'],
    stigma_vital_signs: ['disbelief_blame', 'safety', 'internalised_blame'],
    tags: ['ED', 'drug_seeking', 'fakingexaggerating', 'non_compliant', 'raceracism'],
    intensity: 3,
    delivery_mode: ['script'],
    estimated_time_minutes: 1,
    notes_for_youth: 'You should not have to prove your pain. These are options for when speaking up feels possible and safe. Not using them is also valid.',
  },
  {
    id: 'script_ed_long_waits_triage_v1',
    type: 'script',
    title: 'Asking about long waits without apologizing',
    summary: 'Language to ask what\'s happening when others are called before you.',
    body: '',
    script_lines: [
      '"I\'ve been waiting [X] hours in a lot of pain. Can you tell me where I am in the queue and what the plan is for me?"',
      '"I understand others may be more critical, but it\'s dangerous when my sickle cell pain goes untreated this long. What is the plan?"',
      '"I\'m worried that something about how I look or my diagnosis is affecting how long I\'m waiting. Can you help me understand what\'s happening?"',
    ],
    journey: 'ED',
    phase: 'during',
    module: ['resources_stories_insights'],
    stigma_vital_signs: ['differential_treatment', 'disbelief_blame', 'safety'],
    tags: ['ED', 'long_waits_after_others', 'raceracism'],
    intensity: 2,
    delivery_mode: ['script'],
    estimated_time_minutes: 1,
    notes_for_youth: 'You do not owe the ED gratitude for basic care. These questions are for when asking feels safe enough.',
  },

  // ── AFTER ─────────────────────────────────────────────────────────────
  {
    id: 'ed_after_debrief_normalize_v1',
    type: 'library_item',
    title: 'Making sense of a hard ED visit',
    summary: 'A short debrief that places what happened in a bigger picture, not in your body alone.',
    body: `After ED, you might replay the looks, the questions, the delays, the tone of voice — and wonder if you did something wrong.

Racialized youth with sickle cell describe being disbelieved, watched by security, and accused of exaggerating or seeking drugs, even when in crisis. These are not one-off bad days. They come from anti-Black racism, carceral logics, and systems designed without Black youth and disabled people in mind.

If parts of your visit felt unsafe or humiliating, that matters. It is not a sign that you were too much or not enough. It is evidence of a system failing you.

Some things that can help — when you have capacity:
• Write down what happened, in your own words
• Talk to someone who gets it — a friend, a peer who also navigates this
• Return to this app to log what happened and see it named clearly

You do not have to process this alone, and you do not have to do it right now.`,
    journey: 'ED',
    phase: 'after',
    module: ['reflection_stigma_vital_signs', 'resources_stories_insights'],
    stigma_vital_signs: ['disbelief_blame', 'differential_treatment', 'safety', 'internalised_blame', 'support'],
    tags: ['ED', 'raceracism', 'security_calledwatched', 'drug_seeking', 'long_waits_after_others'],
    intensity: 2,
    delivery_mode: ['passive_read', 'optional_reflection'],
    estimated_time_minutes: 3,
  },
  {
    id: 'ed_after_one_slider_checkin_v1',
    type: 'library_item',
    title: 'One-question check after ED',
    summary: 'A single question for days when you have no energy to debrief.',
    body: `If you only have energy for one thing, you can answer this:

When you think about your last ED visit, how heavy does it feel in your body and mind right now, from 0 to 10?

0 = not heavy at all
10 = extremely heavy

Your answer is for you. If it feels very heavy, it makes sense to move gently today. You can come back to process more when you have capacity — this app will still be here.`,
    journey: 'ED',
    phase: 'after',
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['internalised_blame'],
    tags: ['ED'],
    intensity: 1,
    delivery_mode: ['optional_reflection'],
    estimated_time_minutes: 1,
  },
  {
    id: 'ed_after_what_helped_micro_reflection_v1',
    type: 'library_item',
    title: 'Saving the parts that helped (even a bit)',
    summary: 'Notice and save any support from the visit without erasing the harm.',
    body: `Even in a visit that was mostly harmful, there may have been small pieces that helped — a particular nurse, a phrase someone used, a friend who stayed on FaceTime, a blanket that finally made you less cold.

Noticing these pieces is not about excusing what went wrong. It is about:
• Remembering who and what helped you feel more human
• Having a record you can lean on when planning for next time

If you want, write one or two lines: "Future me — remember that [person/thing] helped when..."

This is yours. It does not have to be formal or complete.`,
    journey: 'ED',
    phase: 'after',
    module: ['patterns_timelines_trends', 'reflection_stigma_vital_signs'],
    stigma_vital_signs: ['support', 'internalised_blame'],
    tags: ['ED', 'raceracism'],
    intensity: 1,
    delivery_mode: ['optional_reflection'],
    estimated_time_minutes: 2,
  },

  // ── COMMUNITY STORIES ─────────────────────────────────────────────────
  {
    id: 'story_ed_too_calm_v1',
    type: 'story',
    title: '"Too calm to be in real pain"',
    summary: 'A 17-year-old Black girl describes being told she was too calm to be in sickle cell crisis.',
    body: `I was 17 the first time a nurse told me I was "too calm to be in real pain." By the time we got to the ED, I had already been in pain for hours at home. I was exhausted. I had my emergency profile on my phone with my usual meds and doses.

At triage, the nurse barely looked at it. She kept asking if I had taken anything at home, like she was trying to catch me in a lie. When I answered quietly, she said, "You seem very calm for a ten out of ten. Are you sure it's really a ten?"

I had been in crisis enough times to know how to breathe through it. That's not calm. That's survival.

They made me wait two and a half hours while people who arrived after me were taken in. My pain reached the point where I couldn't stay still. At that point — when I was visibly distressed — they took me in.

What I've learned: the system often waits for you to perform pain in a specific way before it responds. That performance should not be required. But until it isn't, I keep my emergency profile updated, I bring someone when I can, and I log every visit.

If a nurse tells you that you're "too calm," what they're really saying is that they expected you to look more scared, or more out of control. That is not a medical assessment. That is a bias.`,
    journey: 'ED',
    phase: ['during', 'after'],
    module: ['resources_stories_insights'],
    stigma_vital_signs: ['disbelief_blame', 'differential_treatment', 'internalised_blame', 'safety'],
    tags: ['ED', 'raceracism', 'agetoo_young', 'long_waits_after_others', 'fakingexaggerating'],
    intensity: 3,
    delivery_mode: ['story'],
    estimated_time_minutes: 3,
    age_at_event: 17,
    self_described_identities: ['Black', 'girl', 'disabled'],
    content_warnings: ['racism', 'medical neglect'],
  },
  {
    id: 'story_ed_security_watch_v1',
    type: 'story',
    title: 'Security watched me instead of my pain',
    summary: 'A 19-year-old Black man talks about being watched by security while untreated.',
    body: `When I was 19, I went to the ED alone because my parents were working night shifts. I was doubled over in the waiting room, but after triage, they sat me in a chair close to the security desk.

Every time I shifted or stood up, the guard looked straight at me. He did not look like he was worried I might faint. He looked like he thought I might run.

My pain got worse and I started sweating. Instead of bringing meds, a nurse told me to sit back down and stop pacing. I wasn't pacing — I was trying to find a position that hurt less.

Eventually a different nurse came over, not to assess me, but to tell me security had noted I was "agitated." I was in untreated sickle cell pain. Of course I was agitated.

They finally put me in a room after four hours. The doctor who treated me apologized and said my wait had been "unusually long." He didn't say why.

I know why.

What I want other people to know: if security is watching you, it is not because you are dangerous. It is because the system sees your Black body in pain as a threat. That is a system problem, not a you problem. Document everything you can. Tell someone you trust what happened. And if you can, come back to log it — because these patterns matter.`,
    journey: 'ED',
    phase: ['during', 'after'],
    module: ['resources_stories_insights'],
    stigma_vital_signs: ['differential_treatment', 'safety', 'disbelief_blame', 'support'],
    tags: ['ED', 'raceracism', 'security_calledwatched', 'long_waits_after_others', 'dangerous_aggressive'],
    intensity: 3,
    delivery_mode: ['story'],
    estimated_time_minutes: 3,
    age_at_event: 19,
    self_described_identities: ['Black', 'man'],
    content_warnings: ['racism', 'security presence', 'medical neglect'],
  },
  {
    id: 'story_ed_believed_and_documented_v1',
    type: 'story',
    title: 'The night a doctor believed me and wrote it down',
    summary: 'A 21-year-old describes an ED doctor who believed their pain and made a clear plan.',
    body: `Not every ED experience has been awful. When I was 21, I ended up in a different hospital than usual. I went in already braced for the usual script — long waits, side eye, questions about why I needed opioids.

Instead, the ED doctor sat down and said, "I've read your emergency profile. It looks like when we treat fast and follow this plan, your crises go a lot better. Does this still feel right to you?"

He did not ask me to prove anything. He asked whether the plan still matched my body.

He ordered the meds in the first twenty minutes. He came back to check whether they were working. When they only partially helped, he adjusted the dose without me having to fight for it.

At the end, he updated my chart with a clear note: "Patient has HbSS sickle cell disease. Crisis pain is real and responds to [specific plan]. Please follow this plan in future visits."

I have brought that chart note to three EDs since then. It has made a difference every time.

What I learned: when care goes right, it's usually because someone sees your history as evidence, not as a list of demands. We shouldn't have to earn that — but when it happens, document it. A good chart note from one visit can protect you in the next one.`,
    journey: 'ED',
    phase: ['during', 'after'],
    module: ['resources_stories_insights'],
    stigma_vital_signs: ['support', 'disbelief_blame'],
    tags: ['ED', 'raceracism'],
    intensity: 2,
    delivery_mode: ['story'],
    estimated_time_minutes: 3,
    age_at_event: 21,
    self_described_identities: ['Black', 'woman'],
    content_warnings: ['mentions previous discrimination'],
  },

  // ── NEWCOMER TEEN GUIDE ────────────────────────────────────────────────
  {
    id: 'newcomer_your_pain_is_real_v1',
    type: 'library_item',
    title: 'Your Pain Is Real — A Note for Newcomer Youth',
    summary: 'Moving to a new country while living with pain is hard. This is for you.',
    body: `Moving to a new country can bring many changes — a new language, a new school, feeling far from home. Living with pain on top of that makes things even harder.

You might feel like no one believes you, like you're being ignored, or like doctors don't understand what you're going through.

Let us say this clearly: **Your pain is real. And it matters.**

Many newcomer teens face extra barriers in the health system — including racism, language gaps, and being dismissed. These are system failures, not failures of your body or your story.

This guide was created with input from youth who have lived with pain and know how hard it is to get care that feels safe and respectful.

You deserve care that listens. You are not alone. We see you. We believe you. And we're with you.

— Adapted from "It's Not All In Your Head: A Guide for Newcomer Teens Living with Pain" (Chronic Pain Network, 2024)`,
    journey: 'ED',
    phase: ['before', 'after'],
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['internalised_blame', 'disbelief_blame', 'support'],
    tags: ['newcomer', 'raceracism', 'internalised_blame', 'affirmation'],
    intensity: 1,
    delivery_mode: ['passive_read'],
    estimated_time_minutes: 2,
  },
  {
    id: 'newcomer_stigma_discrimination_v1',
    type: 'library_item',
    title: 'Stigma and Discrimination in Pain Care',
    summary: 'What stigma is, how it shows up in healthcare, and why it\'s not your fault.',
    body: `**Stigma** means being judged or treated unfairly because of something about you — like being in pain, living with a chronic illness, disability, or a mental health condition.

**Discrimination** happens when someone treats you worse because of who you are — your race, religion, gender, language, or where you're from. These things often show up in the health system.

Teens who are Black, Brown, Disabled, 2SLGBTQIA+/Queer, new to Canada, or practice another religion may be more likely to have their pain ignored or not taken seriously.

**But it's not your fault. And it's not in your head.**

Examples you might recognize:
• The doctor talks to your parent instead of asking how you feel
• You're told your pain is "just stress" from moving to a new country
• You feel like you have to be quiet and not ask for help
• You don't get a translator, so it's hard to understand what's going on

All pain deserves treatment. This is your right — and the system failing you says nothing about the realness of your pain.`,
    journey: 'ED',
    phase: ['before', 'during', 'after'],
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['disbelief_blame', 'differential_treatment', 'internalised_blame'],
    tags: ['raceracism', 'stigma', 'newcomer', 'disbelief'],
    intensity: 2,
    delivery_mode: ['passive_read', 'optional_reflection'],
    estimated_time_minutes: 3,
  },
  {
    id: 'newcomer_rights_canadian_healthcare_v1',
    type: 'library_item',
    title: 'Your Rights in Canadian Healthcare',
    summary: 'A clear list of what you are entitled to — from interpreters to informed consent.',
    body: `In Canada, you have the right to:

• Be treated with respect and dignity
• Get care without discrimination or racism
• Say how you feel and be listened to
• Ask questions and get clear answers
• Say no to treatments that don't feel right for you
• Get help in your language (hospitals must provide an interpreter for free)
• Ask for care that honours your culture, traditions, and healing practices

**Informed Consent** means that healthcare providers need your permission before treating you. This means:
• You have the right to understand what's being done
• You have the right to say yes or no
• You can ask for more time, more information, or a different health professional
• No one should touch your body or do procedures without your clear permission
• You can ask for a healthcare professional of a different gender if you'd feel more comfortable

**If someone treats you unfairly or makes you feel unsafe:**
• Tell someone you trust
• Ask to speak to a patient advocate
• You can file a complaint — even as a teen

You are the expert in your body. You deserve care that sees all of you.`,
    journey: 'ED',
    phase: ['before', 'during', 'after'],
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['safety', 'disbelief_blame'],
    tags: ['rights', 'newcomer', 'raceracism', 'consent', 'advocacy'],
    intensity: 1,
    delivery_mode: ['passive_read', 'checklist'],
    estimated_time_minutes: 3,
  },
  {
    id: 'newcomer_3sentence_health_summary_v1',
    type: 'library_item',
    title: 'The 3-Sentence Health Summary',
    summary: 'A simple script to help you be heard — especially when you\'re nervous or overwhelmed.',
    body: `When you're in pain, it can be hard to explain everything — especially if you're nervous, overwhelmed, or still learning English. This 3-sentence format helps you speak up and be understood.

**Use this at any appointment or in the ED:**

**1. This is what's been happening (symptom) in this part of my body (location) for X months/days.**
Example: *"I've been having a sharp pain crisis in my back and arms for the last two days."*

**2. This is how it's affecting my life.**
Example: *"I can't sleep, eat, or go to school. I've been crying from the pain."*

**3. This is what I need help with today.**
Example: *"I need IV pain medication and for someone to actually listen to me."*

---

You can write it down before your appointment, practice with a trusted person, or bring someone to help say it with you.

This summary helps you take control — and helps your care team listen better.

Remember: You can always ask:
• "Can you explain that in a simpler way?"
• "Can I take a moment to think?"`,
    journey: 'ED',
    phase: ['before', 'during'],
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['safety', 'support'],
    tags: ['communication', 'newcomer', 'advocacy', 'preparation'],
    intensity: 1,
    delivery_mode: ['script', 'checklist'],
    estimated_time_minutes: 2,
  },
  {
    id: 'newcomer_pain_looks_different_v1',
    type: 'library_item',
    title: 'Pain Looks Different on Everyone',
    summary: 'Your cultural background shapes how you express pain — that\'s valid, and it matters.',
    body: `Pain doesn't always look the same from one person to the next. Depending on your background, culture, or personal experiences, the way you show, talk about, or deal with your pain might be different — **and that is okay.**

Unfortunately, not everyone — including some healthcare providers — always understands these differences. This can lead to people feeling unheard or misunderstood when they're in pain.

**Pain is shaped by many things:**
• Your overall health
• Stress, anxiety, or mood
• Life changes like moving to a new country and starting a new school
• Cultural beliefs about how you should express your pain
• How much your pain has been believed or dismissed before

Some teens with chronic pain might seem irritable, withdrawn, or spend more time alone. Others stay quiet. Some cry openly. Others hold it together while screaming inside.

**All of these are valid. None of them make your pain less real.**

If a provider seems surprised by how you're expressing (or not expressing) your pain, you can say:
*"This is how I experience pain. It doesn't mean it's less serious."*`,
    journey: 'ED',
    phase: ['before', 'during', 'after'],
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['disbelief_blame', 'internalised_blame'],
    tags: ['raceracism', 'newcomer', 'stigma', 'cultural_safety'],
    intensity: 1,
    delivery_mode: ['passive_read', 'optional_reflection'],
    estimated_time_minutes: 2,
  },
  {
    id: 'newcomer_why_dont_they_believe_me_v1',
    type: 'library_item',
    title: 'Why Don\'t People Believe I\'m in Pain?',
    summary: 'The honest, systemic answer — and why it\'s not about you.',
    body: `Because the health system, school system, employment system — all systems — weren't built for everyone.

Some doctors may only believe pain when they can see it with a scan or blood work. They may also be influenced by harmful beliefs that say people from certain backgrounds "feel less pain" (which is wrong), or that teens are "too young" to have real pain (also wrong).

**For sickle cell specifically:** Black youth with sickle cell have faced decades of being accused of drug-seeking, overdramatizing, or faking. These are racist myths that have caused real harm and real death. The disbelief you may face is not a reflection of your pain — it's a failure of a system built without you in mind.

**What's happening when you feel dismissed:**
• Implicit racial bias affecting how providers assess pain
• Providers using visible distress as the main measure of pain severity
• Old, incorrect studies claiming racial differences in pain tolerance
• Systems that were never designed to be culturally safe

**This is not your fault.** You are not dramatic. You are not making it up. You are not asking for too much.

Please don't give up. There are people who will believe you. And we are changing this system together.`,
    journey: 'ED',
    phase: ['during', 'after'],
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['disbelief_blame', 'differential_treatment', 'internalised_blame'],
    tags: ['raceracism', 'disbelief', 'anti_black_racism', 'systemic_racism', 'sickle_cell'],
    intensity: 2,
    delivery_mode: ['passive_read', 'optional_reflection'],
    estimated_time_minutes: 3,
    content_warnings: ['discusses anti-Black racism in healthcare', 'mentions medical harm'],
  },
  {
    id: 'newcomer_tests_and_scans_v1',
    type: 'library_item',
    title: 'What Tests and Scans Can (and Can\'t) Show',
    summary: 'A "normal" result doesn\'t mean your pain isn\'t real. Here\'s why.',
    body: `Sometimes doctors use blood tests, X-rays, MRIs, or CT scans to try to understand your pain. But **not all pain shows up on these tests — and that's okay.**

**What to know:**
• Some pain (like nerve pain, sickle cell complications, stomach pain, or migraine) doesn't always appear in test results
• If your test is "normal," your pain is still real and deserves care
• You can still get help, even without a clear diagnosis

**You can say:**
*"Even if the test doesn't show it, the pain is still affecting my life. I need support."*

Or:
*"In my previous crises, tests haven't always captured what was happening. My body tells me more than the tests do."*

This matters especially for sickle cell — a crisis can be excruciating even when imaging looks unchanged. Your experience of your body is valid data. You have been living in this body your entire life.`,
    journey: 'ED',
    phase: 'during',
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['disbelief_blame', 'safety'],
    tags: ['tests', 'disbelief', 'sickle_cell', 'advocacy', 'ED'],
    intensity: 1,
    delivery_mode: ['passive_read', 'script'],
    estimated_time_minutes: 2,
  },
  {
    id: 'newcomer_3p_approach_v1',
    type: 'library_item',
    title: 'Managing Pain: The 3P Approach + Your Cultural Tools',
    summary: 'Three types of pain strategies — plus the ones from your own culture that already work.',
    body: `The most effective way to manage pain includes a mix of three types of strategies — called the **3Ps**:

**Psychological** — Things that help your mind and nervous system
• Deep breathing, guided imagery, or distraction techniques
• Listening to music, meditating, praying, drawing, journalling
• Finding comfort and support from family and community

**Physical** — Things that help your body directly
• Heat packs, cold packs, resting, or elevating an injured area
• Acupuncture, massage, stretching, gentle movement (Tai Chi, yoga)
• Warm baths or showers during a crisis

**Pharmacological** — Medications that help
• Over-the-counter options (like Tylenol or Advil for mild pain)
• Prescription medications from your doctor
• Herbal balms, oils, teas, and nourishing soups from your own tradition

**Your culture, your tools.** You may already have ways of managing pain that work for you — and those tools are just as important. Managing pain shouldn't mean changing everything about your life. It should support your routines, values, and culture.

When these strategies are used together, you can get the most relief. Ask your care team which combination makes sense for your situation.`,
    journey: 'ED',
    phase: ['before', 'after'],
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['support'],
    tags: ['coping', 'newcomer', 'cultural_safety', 'pain_management'],
    intensity: 1,
    delivery_mode: ['passive_read', 'optional_reflection'],
    estimated_time_minutes: 3,
    gif: '__breathing__',
  },
  {
    id: 'newcomer_navigating_healthcare_levels_v1',
    type: 'library_item',
    title: 'Navigating Canadian Healthcare: Where to Go for What',
    summary: 'Emergency, primary, community, and specialty care — a quick guide for newcomer teens.',
    body: `If you're new to Canada and living with pain, it can be hard to know where to start. Here's a breakdown:

**Emergency Care (ED/ER)**
Go here if you're in severe pain, having a sickle cell crisis, have a serious injury, or are in a medical emergency. Call 911 if it's serious — even if you're unsure. Calling 911 is free. (If an ambulance brings you to hospital, there may be a small fee in some provinces — a social worker can help if cost is a concern.)

**Primary Care**
Your first stop for ongoing pain help: family doctors, nurse practitioners, or walk-in clinics. They can give prescriptions and refer you to specialists. If you don't have a family doctor, a walk-in clinic or community health centre is a good starting point.

**Community Care**
Local services like community health centres, school clinics, or newcomer health programs. Often free or low-cost. Helpful if you need support beyond just medical — including language help, mental health, housing, or social support.

**Specialty / Tertiary Care**
Hospital-based care for more serious or long-term conditions. You usually need a referral to see specialists like a **hematologist** (blood disorders like sickle cell), **neurologist** (nerve pain, migraine), or **rheumatologist** (joint and muscle conditions).

**Waitlists are real.** While you wait for a specialist, keep tracking your symptoms, ask for interim support from your family doctor, and use tools like the Power Over Pain Portal (poweroverpain.ca) — free and made for Canadian youth.`,
    journey: 'ED',
    phase: 'before',
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['safety', 'support'],
    tags: ['newcomer', 'navigation', 'Canadian_health_system', 'ED', 'sickle_cell'],
    intensity: 1,
    delivery_mode: ['passive_read', 'checklist'],
    estimated_time_minutes: 3,
  },
  {
    id: 'newcomer_when_system_hurts_you_v1',
    type: 'library_item',
    title: 'When the System Still Hurts You',
    summary: 'Sometimes you do everything right and still get dismissed. Here\'s what to do next.',
    body: `Sometimes, even when you do everything right — you explain your pain clearly, you stay calm, you bring someone with you — you're still not heard. That can hurt deeply.

**If that happens, here are your options:**

• **Tell someone you trust** — a school counsellor, support worker, family member, or Elder
• **Ask for a patient advocate** — many hospitals have staff whose job is to help you speak up and navigate the system
• **Look for local newcomer or youth groups** — community organizations often know how to find better, safer care
• **You can file a complaint** — even as a teen. Ask the hospital's patient relations office or a social worker how.

**If you feel unsafe or experience serious harm:**
• In Ontario, the Ontario Human Rights Commission handles complaints about discrimination in health care
• Settlement agencies and community health centres often have advocates who can help

You are allowed to be angry. You are allowed to say "that was wrong." You are allowed to ask for different care.

Every time you speak up, share your story, or ask for help, you are helping change the system for everyone who comes after you.

**You are trying your best. You are not making it up. You deserve to be believed and helped.**`,
    journey: 'ED',
    phase: ['during', 'after'],
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['disbelief_blame', 'differential_treatment', 'internalised_blame', 'support'],
    tags: ['raceracism', 'advocacy', 'harm', 'newcomer', 'complaint', 'systemic_racism'],
    intensity: 2,
    delivery_mode: ['passive_read', 'optional_reflection'],
    estimated_time_minutes: 3,
    content_warnings: ['discusses systemic harm and discrimination'],
  },
  {
    id: 'newcomer_equity_vs_equality_v1',
    type: 'library_item',
    title: 'Equity vs. Equality: What\'s the Difference?',
    summary: 'Understanding why "same treatment" isn\'t the same as "fair treatment."',
    body: `Life isn't the same for everyone. Some people face unfair treatment because of their race, religion, gender, disability, income, or because they're new to Canada. That means they may need different kinds of support to have the same chances as others.

**Equality = Everyone gets the same thing**
It sounds fair — but it doesn't always work, because not everyone starts in the same place.

Example: If everyone gets the same-size shoes, but some people have different-sized feet, that's not actually fair — even though it's equal.

**Equity = Everyone gets what they need**
Equity means giving each person what they specifically need — even if it's different from what others get.

Example: Equity means giving each person the right shoe size for their feet. That way, everyone can walk comfortably.

---

**In healthcare, this looks like:**
• Some people need interpreters to understand their care (equity), not just the same appointment in English (equality)
• Some people need trauma-informed providers who understand sickle cell and racism (equity), not just a standard pain protocol
• Some people need longer appointments or written summaries (equity), not just the same rushed 10 minutes everyone gets

Knowing this difference can help you understand your rights, speak up, and name unfair systems around you.

You are not asking for more than you deserve. You are asking for what everyone needs — fairness.`,
    journey: 'ED',
    phase: ['before', 'after'],
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['disbelief_blame', 'internalised_blame', 'support'],
    tags: ['raceracism', 'newcomer', 'equity', 'rights'],
    intensity: 1,
    delivery_mode: ['passive_read', 'optional_reflection'],
    estimated_time_minutes: 2,
    gif: '__equity__',
  },
  {
    id: 'newcomer_glossary_v1',
    type: 'library_item',
    title: 'Glossary: Words You Might Hear in Healthcare',
    summary: 'Plain-language definitions for health system terms — especially for newcomer youth.',
    body: `**Advocate** — Someone who helps you speak up for your rights or get the care you need. Could be a social worker, community worker, or trusted adult.

**Acute pain** — Pain that comes on suddenly, often after an injury or crisis. Usually lasts a short time (though a sickle cell crisis can be intense and acute).

**Chronic pain** — Pain that lasts more than 3 months. May come and go, or be there all the time. Chronic pain is real, even if others can't see it.

**Coping strategy** — Something you do to handle stress or pain, like deep breathing, stretching, journalling, or talking to someone you trust.

**Culturally safe care** — Care that respects your background, identity, language, and values — and makes you feel safe, understood, and supported.

**Informed consent** — Your right to understand what will happen before you get medical care. You must be told what the treatment is, why it's being done, and what your options are. You can say yes, no, or ask for more information.

**Intersectionality** — How different parts of your identity (race, gender, class, religion, disability, immigration status) connect — and how those connections affect how you're treated.

**Multidisciplinary care** — When different health professionals (doctors, nurses, therapists, social workers) work together as a team to help with all parts of your health.

**Primary care** — Your first point of contact: a family doctor, nurse practitioner, or walk-in clinic. They help with general health, prescriptions, and referrals.

**Referral** — When one doctor sends you to a specialist for more help.

**Stigma** — Being judged, blamed, or treated unfairly because of something about you — like having pain, a disability, or mental health struggles.

**Systemic discrimination** — When unfair treatment is built into rules, institutions, or policies, making it harder for some people to get care, housing, jobs, or respect.

**Triage** — How hospitals decide how quickly people need care. If you have very serious symptoms, you may be seen faster. If not urgent, you may need to wait. (If you feel undertriaged, you can speak up.)`,
    journey: 'ED',
    phase: ['before', 'during', 'after'],
    module: ['reflection_stigma_vital_signs'],
    stigma_vital_signs: ['safety', 'support'],
    tags: ['newcomer', 'glossary', 'navigation', 'rights'],
    intensity: 1,
    delivery_mode: ['passive_read'],
    estimated_time_minutes: 3,
  },
];

export function getContentByPhase(phase: ContentPhase): EDContentItem[] {
  return ED_CONTENT.filter(item =>
    Array.isArray(item.phase) ? item.phase.includes(phase) : item.phase === phase
  );
}

export function getRecommendations(
  safetyScore: number,
  activeFlags: string[],
  maxItems = 2
): EDContentItem[] {
  const flagSet = new Set(activeFlags.map(f => f.toLowerCase()));

  const hasSecurityFlag = activeFlags.some(f =>
    f.toLowerCase().includes('security') || f.toLowerCase().includes('watch')
  );
  const hasDrugSeekingFlag = activeFlags.some(f =>
    f.toLowerCase().includes('drug') || f.toLowerCase().includes('seek')
  );

  let pool = ED_CONTENT.filter(item => item.journey === 'ED');

  if (safetyScore <= 3) {
    pool = pool.filter(item =>
      item.delivery_mode.some(m => ['grounding', 'optional_reflection', 'passive_read'].includes(m)) &&
      item.intensity <= 2
    );
  }

  const scored = pool.map(item => {
    let score = 0;
    if (hasDrugSeekingFlag && item.tags.includes('drug_seeking')) score += 3;
    if (hasSecurityFlag && item.tags.includes('security_calledwatched')) score += 3;
    for (const f of activeFlags) {
      const fl = f.toLowerCase();
      for (const tag of item.tags) {
        if (tag.toLowerCase().includes(fl) || fl.includes(tag.toLowerCase())) score += 1;
      }
    }
    if (safetyScore <= 3 && item.delivery_mode.includes('grounding')) score += 2;
    return { item, score };
  });

  scored.sort((a, b) => b.score - a.score);

  const top = scored.slice(0, maxItems * 2).map(s => s.item);
  const seen = new Set<string>();
  const result: EDContentItem[] = [];
  for (const item of top) {
    if (!seen.has(item.id) && result.length < maxItems) {
      seen.add(item.id);
      result.push(item);
    }
  }
  return result;
}

export const INTENSITY_ICONS: Record<1 | 2 | 3, string> = {
  1: '🟢',
  2: '🟡',
  3: '🔴',
};

export const DELIVERY_ICONS: Record<string, string> = {
  passive_read: '📖',
  optional_reflection: '💭',
  script: '🎯',
  checklist: '✅',
  grounding: '🧘',
  story: '📖',
};

export const PHASE_LABELS: Record<ContentPhase, string> = {
  before: 'Before',
  during: 'During',
  after: 'After',
};
